import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Stack,
  Pagination,
  useTheme,
  Collapse,
  IconButton
} from '@mui/material';
import { Search, Save, ChevronDown, ChevronRight } from 'lucide-react';
import { useGlobalConfig } from 'context/GlobalContext';
import { ApplicationMasterApis } from 'apis/applicationMasterAPI';
import { DAApplicationMaster, DAApplicationMasterQueryParams } from 'types/apis/applicationMasterInterfaces';
import { AppWindow } from 'lucide-react';
import { CirclePlus } from 'lucide-react';


export const ApplicationScopeMaster = () => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  
  // State management
  const [masterData, setMasterData] = useState<DAApplicationMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  
  // Pagination settings
  const itemsPerPage = 50;

  // Fetch data from API
  const fetchMasterData = async (page: number = 1, search: string = '') => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams: DAApplicationMasterQueryParams = {
        page,
        per_page: itemsPerPage,
        sort_by: 'product',
        sort_order: 'asc'
      };

      // Add search filters if provided
      if (search.trim()) {
        // Search in product, feature, and description fields
        queryParams.product = search;
        queryParams.feature = search;
      }

      const response = await ApplicationMasterApis.getDAApplicationMaster(rc, queryParams);
      
      if (response.error) {
        setError(response.error || 'Failed to fetch master data');
      } else {
        const masterData = response.data?.data || [];
        setMasterData(masterData);
        
        if (response.data?.meta?.pagination) {
          setTotalPages(response.data.meta.pagination.pageCount);
        }
        
        // Expand only the first product by default
        if (masterData.length > 0) {
          const firstProduct = masterData[0].product;
          setExpandedProducts(new Set([firstProduct]));
        }
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchMasterData(currentPage, searchQuery);
  }, [currentPage]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // For search, we'll use client-side filtering instead of API calls
      // This provides instant results and reduces API load
      setCurrentPage(1);
      // No need to refetch data - we already have all the data loaded
      // Client-side filtering will handle showing/hiding results
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Handle search button click
  const handleSearch = () => {
    setCurrentPage(1);
    // With client-side filtering, we don't need to refetch data
    // The search will be handled by the groupedData useMemo
  };

  // Handle search on Enter key
  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Parse comma-separated values into chips
  const renderChips = (values: string, color: 'primary' | 'secondary' | 'success' = 'primary') => {
    if (!values) return null;
    
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {values.split(',').map((value, index) => (
          <Chip
            key={index}
            label={value.trim()}
            size="small"
            color={color}
            variant="outlined"
            sx={{ fontSize: '0.75rem', height: '24px', fontWeight: 600 }}
          />
        ))}
      </Stack>
    );
  };

  // Group data by product with client-side filtering
  const groupedData = React.useMemo(() => {
    const groups: { [key: string]: DAApplicationMaster[] } = {};
    
    // Filter data based on search query (client-side fallback)
    const filteredData = masterData.filter(item => {
      if (!searchQuery.trim()) return true;
      
      const searchLower = searchQuery.toLowerCase();
      return (
        (item.product || '').toLowerCase().includes(searchLower) ||
        (item.product_key || '').toLowerCase().includes(searchLower) ||
        (item.feature || '').toLowerCase().includes(searchLower) ||
        (item.description || '').toLowerCase().includes(searchLower) ||
        (item.customer_type || '').toLowerCase().includes(searchLower) ||
        (item.fuel_type || '').toLowerCase().includes(searchLower) ||
        (item.electric_meter_type || '').toLowerCase().includes(searchLower)
      );
    });
    
    filteredData.forEach(item => {
      if (!groups[item.product]) {
        groups[item.product] = [];
      }
      groups[item.product].push(item);
    });
    
    return groups;
  }, [masterData, searchQuery]);

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // Toggle product expansion
  const toggleProductExpansion = (productName: string) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productName)) {
        newSet.delete(productName);
      } else {
        newSet.add(productName);
      }
      return newSet;
    });
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Data Table */}
        <Card sx={{ borderRadius: 2, p:2, textAlign:'left' }}>
           {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1, display: 'flex', alignItems: 'center' }}>
              <AppWindow size={24} style={{ stroke: theme.palette.primary.main, marginRight: 10 }} />
               Application Scope
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Manage the availability of products and features across different customer segments.
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Save size={16} />}
              sx={{ 
                color: 'text.secondary',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                }
              }}
            >
              Save All Changes
            </Button>
            <Button
              variant="contained"
              startIcon={<CirclePlus style={{ stroke: theme.palette.primary.contrastText }} size={16} />}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }}
            >
              Add Feature
            </Button>
          </Box>
        </Box>

        {/* Search */}
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent sx={{ py: 2 , bgcolor:theme.palette.secondary.main}}>
            <TextField
              fullWidth
              placeholder="Search by product, product key, feature, description, customer type, fuel type, or meter type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} color={theme.palette.text.secondary} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={clearSearch}
                      sx={{ p: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ color: 'text.secondary', cursor: 'pointer' }}>
                        ✕
                      </Typography>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {searchQuery && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Found {Object.values(groupedData).flat().length} results for "{searchQuery}"
              </Typography>
            )}
          </CardContent>
        </Card>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <CardContent sx={{ p: 0 }}>
                {Object.keys(groupedData).length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchQuery ? 
                        `No results found for "${searchQuery}". Try adjusting your search criteria.` : 
                        'No master data available. Try adjusting your search criteria.'
                      }
                    </Typography>
                    {searchQuery && (
                      <Button 
                        variant="outlined" 
                        onClick={clearSearch}
                        sx={{ mt: 2 }}
                      >
                        Clear Search
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Box>
                    {Object.entries(groupedData).map(([productName, items]) => {
                      const isExpanded = expandedProducts.has(productName);
                      
                      return (
                        <Box key={productName} sx={{ mb: 2 }}>
                          {/* Product Header - Clickable */}
                          <Box 
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 2,
                              bgcolor: theme.palette.secondary.main,
                              border: '1px solid',
                              borderColor: theme.palette.divider,
                              borderRadius: 1,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.9 }
                            }}
                            onClick={() => toggleProductExpansion(productName)}
                          >
                            <IconButton size="small" sx={{ p: 0 }}>
                              {isExpanded ? 
                                <ChevronDown size={18} color={theme.palette.text.primary} /> : 
                                <ChevronRight size={18} color={theme.palette.text.primary} />
                              }
                            </IconButton>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', textTransform: 'uppercase' }}>
                              {productName} ({items.length})
                            </Typography>
                          </Box>
                          
                          {/* Collapsible Content */}
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ 
                              border: '1px solid',
                              borderColor: theme.palette.divider,
                              borderTop: 'none',
                              borderRadius: '0 0 4px 4px'
                            }}>
                              {/* Table Header */}
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 100px',
                                gap: 2,
                                px: 3,
                                py: 2,
                                bgcolor: theme.palette.secondary.main,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                alignItems: 'center'
                              }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Component
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Customer Types
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Fuel Types
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Meter Types
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Component Technical Name
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Actions
                                </Typography>
                              </Box>
                              
                              {/* Table Rows */}
                              {items.map((item, index) => (
                                <Box 
                                  key={item.id}
                                  sx={{ 
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 100px',
                                    gap: 2,
                                    px: 3,
                                    py: 2,
                                    bgcolor: theme.palette.secondary.main,
                                    alignItems: 'center',
                                    borderBottom: index < items.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                    '&:hover': {
                                      opacity: .9
                                    }
                                  }}
                                >
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                      {item.feature}
                                    </Typography>
                                    {item.description && (
                                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        {item.description}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box>
                                    {renderChips(item.customer_type, 'primary')}
                                  </Box>
                                  <Box>
                                    {renderChips(item.fuel_type, 'secondary')}
                                  </Box>
                                  <Box>
                                    {renderChips(item.electric_meter_type, 'secondary')}
                                  </Box>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                      {item.product_key}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="body2" color="text.secondary">
                                      ...
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </CardContent>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                  />
                </Box>
              )}
            </>
          )}
        </Card>
      </Box>
    </Box>
  );
};